import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowRight, Award, Eye, Globe2, ShieldCheck, Trophy, Users, Zap } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import CountrySpotlights from "../components/CountrySpotlights";
import TrendingByNation from "../components/TrendingByNation";
import { useTranslation } from "react-i18next";

const STADIUM = "https://images.pexels.com/photos/37635539/pexels-photo-37635539.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const HERO = "https://images.unsplash.com/photo-1613125479775-0c09b9a46ae1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxhZnJpY2FuJTIweW91dGglMjBmb290YmFsbCUyMHBsYXllciUyMGFjdGlvbnxlbnwwfHx8fDE3ODM0NjA3MDJ8MA&ixlib=rb-4.1.0&q=85";
const TRAINING = "https://images.unsplash.com/photo-1652665314612-c48e10a01598?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxhZnJpY2FuJTIweW91dGglMjBmb290YmFsbCUyMHBsYXllciUyMGFjdGlvbnxlbnwwfHx8fDE3ODM0NjA3MDJ8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useTranslation();
  const [topCountries, setTopCountries] = useState([]);
  useEffect(() => {
    api.get("/countries/stats").then((r) => setTopCountries(r.data.slice(0, 8))).catch(() => {});
  }, []);
  const PATHWAY = [
    { step: "01", label: t("pathway.step1"), desc: t("pathway.step1Body") },
    { step: "02", label: t("landing.valueCard2Title"), desc: t("landing.valueCard2Body") },
    { step: "03", label: t("pathway.step2"), desc: t("pathway.step2Body") },
    { step: "04", label: t("pathway.step4"), desc: t("pathway.step4Body") },
    { step: "05", label: t("pathway.step3"), desc: t("pathway.step3Body") },
  ];
  const TESTIMONIALS = [
    t("landing.testimonial1"),
    t("landing.testimonial2"),
    t("landing.testimonial3"),
    t("landing.testimonial4"),
    t("landing.testimonial5"),
  ];
  return (
    <div className="grain min-h-screen text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${STADIUM})` }}
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-28 md:pt-32 md:pb-40 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border border-white/20 px-3 py-1 text-xs tracking-[0.25em] uppercase mb-6">
              <span className="w-2 h-2 rounded-full bg-[#e65f2b] animate-pulse" />
              {t("hero.badge")}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter uppercase leading-[0.95]">
              {t("hero.line1")}<br />
              {t("hero.line2")} <span className="text-[#eab308]">{t("hero.lagos")}</span>,<br />
              {t("hero.line3")} <span className="text-[#e65f2b]">{t("hero.madrid")}</span>.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/70 font-medium">
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/register" data-testid="hero-cta-player">
                <Button className="bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold rounded-sm h-12 px-6 text-base uppercase tracking-widest">
                  {t("hero.ctaPlayer")} <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/register" data-testid="hero-cta-scout">
                <Button variant="outline" className="border-white/20 h-12 px-6 rounded-sm text-white hover:bg-white/10 font-bold uppercase tracking-widest">
                  {t("hero.ctaScout")}
                </Button>
              </Link>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-8 max-w-xl">
              {[
                ["54", t("hero.statNations")],
                ["1000+", t("hero.statVerified")],
                ["120+", t("hero.statClubs")],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-4xl font-bold text-[#eab308]">{n}</div>
                  <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 relative">
            <div className="relative aspect-[4/5] border border-white/10 overflow-hidden">
              <img src={HERO} alt={t("profile.heroAlt")} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="text-xs uppercase tracking-[0.25em] text-white/60">{t("hero.featuredOverline")}</div>
                <div className="font-display text-2xl font-bold mt-1">{t("hero.featuredName")}</div>
                <div className="text-sm text-white/80">{t("hero.featuredRole")} · {t("common.verified")}</div>
              </div>
              <div className="absolute top-4 right-4 flex items-center gap-1 bg-[#eab308] text-black px-2 py-1 text-xs font-bold uppercase tracking-widest">
                <ShieldCheck size={14} /> {t("common.verified")}
              </div>
            </div>
          </div>
        </div>

        <div className="stripe-bar h-2 w-full" />
      </section>

      {/* HOW IT WORKS */}
      <section id="pathway" className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-14 grid md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b]">{t("pathway.overline")}</div>
            <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold tracking-tight uppercase">
              {t("pathway.title")}<br />{t("pathway.subtitle")}
            </h2>
          </div>
          <p className="md:col-span-5 text-white/70">
            {t("pathway.body")}
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-4">
          {PATHWAY.map((p, i) => (
            <div
              key={p.step}
              className="border border-white/10 bg-[#141414] p-6 hover:border-[#e65f2b] transition-colors group"
              data-testid={`pathway-step-${i}`}
            >
              <div className="font-display text-4xl font-bold text-[#e65f2b] group-hover:text-[#eab308] transition-colors">{p.step}</div>
              <div className="mt-3 font-display uppercase tracking-tight text-xl font-bold">{p.label}</div>
              <p className="mt-2 text-sm text-white/60">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BENTO FEATURES */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-12 gap-4 md:gap-6 auto-rows-[180px]">
          <div className="md:col-span-7 md:row-span-2 border border-white/10 bg-[#141414] p-8 relative overflow-hidden">
            <img src={TRAINING} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="relative">
              <Award className="text-[#eab308]" size={32} />
              <h3 className="mt-6 font-display text-3xl uppercase font-bold tracking-tight">{t("landing.valueCard2Title")}</h3>
              <p className="mt-3 text-white/70 max-w-md">{t("landing.valueCard2Body")}</p>
            </div>
          </div>
          <div className="md:col-span-5 border border-white/10 bg-[#141414] p-6">
            <Eye className="text-[#e65f2b]" size={28} />
            <h3 className="mt-4 font-display text-xl uppercase font-bold">{t("scoutDashboard.filter.country")} · {t("scoutDashboard.filter.position")}</h3>
            <p className="mt-1 text-sm text-white/60">{t("landing.valueSub")}</p>
          </div>
          <div className="md:col-span-5 border border-white/10 bg-[#141414] p-6">
            <Zap className="text-[#2A9D8F]" size={28} />
            <h3 className="mt-4 font-display text-xl uppercase font-bold">{t("profile.radar")}</h3>
            <p className="mt-1 text-sm text-white/60">{t("landing.valueCard1Body")}</p>
          </div>
          <div className="md:col-span-4 border border-white/10 bg-[#141414] p-6">
            <Globe2 className="text-[#eab308]" size={28} />
            <h3 className="mt-4 font-display text-xl uppercase font-bold">{t("profile.passport")}</h3>
            <p className="mt-1 text-sm text-white/60">{t("pathway.step3Body")}</p>
          </div>
          <div className="md:col-span-4 border border-white/10 bg-[#141414] p-6">
            <Users className="text-[#e65f2b]" size={28} />
            <h3 className="mt-4 font-display text-xl uppercase font-bold">{t("landing.valueCard2Title")}</h3>
            <p className="mt-1 text-sm text-white/60">{t("landing.valueSub")}</p>
          </div>
          <div className="md:col-span-4 border border-white/10 bg-[#141414] p-6">
            <Trophy className="text-[#2A9D8F]" size={28} />
            <h3 className="mt-4 font-display text-xl uppercase font-bold">{t("landing.valueCard3Title")}</h3>
            <p className="mt-1 text-sm text-white/60">{t("landing.valueCard3Body")}</p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS MARQUEE */}
      <section className="border-y border-white/10 py-10 bg-[#0f0f0f]">
        <div className="marquee">
          <div className="marquee-track font-display uppercase text-2xl md:text-3xl text-white/70">
            {[...TESTIMONIALS, ...TESTIMONIALS].map((quote, i) => (
              <span key={`${quote}-${i}`} className="flex items-center gap-8">
                <span>{quote}</span>
                <span className="text-[#e65f2b]">◆</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* COUNTRY SPOTLIGHTS - Verified players grouped by country */}
      <CountrySpotlights />

      {/* TRENDING BY NATION - top 3 verified per country this week */}
      <TrendingByNation />

      {/* TOP COUNTRIES */}
      {topCountries.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-24">
          <div className="grid md:grid-cols-12 gap-6 items-end mb-10">
            <div className="md:col-span-8">
              <div className="text-xs uppercase tracking-[0.3em] text-[#e65f2b]">{t("landing.countriesOverline")}</div>
              <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold uppercase tracking-tight">
                {t("landing.countriesTitle")}
              </h2>
            </div>
            <p className="md:col-span-4 text-white/70">{t("landing.countriesSub")}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {topCountries.map((c) => (
              <Link
                key={c.country}
                to={`/country/${encodeURIComponent(c.country)}`}
                data-testid={`top-country-${c.country}`}
                className="border border-white/10 bg-[#141414] p-5 hover:border-[#e65f2b] transition-colors group"
              >
                <div className="font-display text-2xl uppercase font-bold tracking-tight group-hover:text-[#e65f2b]">{c.country}</div>
                <div className="mt-2 flex items-baseline gap-3 text-sm text-white/60">
                  <span><span className="text-white font-bold">{c.count}</span> {t("nav.players").toLowerCase()}</span>
                  <span className="text-[#eab308]">{c.verified} {t("common.verified").toLowerCase()}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section id="about" className="max-w-7xl mx-auto px-6 py-24">
        <div className="border border-white/10 bg-gradient-to-br from-[#1a1a1a] to-[#0e0e0e] p-10 md:p-16 relative overflow-hidden">
          <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#e65f2b]/10 blur-3xl" />
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="text-xs uppercase tracking-[0.3em] text-[#eab308]">{t("cta.overline")}</div>
              <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold uppercase tracking-tight">
                {t("cta.title")}
              </h2>
              <p className="mt-4 text-white/70 max-w-2xl">
                {t("hero.subtitle")}
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              <Link to="/register" data-testid="footer-cta-register">
                <Button className="w-full bg-[#e65f2b] hover:bg-[#c54a1c] text-black font-bold uppercase tracking-widest rounded-sm h-12">
                  {t("cta.createProfile")}
                </Button>
              </Link>
              <Link to="/players" data-testid="footer-cta-browse">
                <Button variant="outline" className="w-full border-white/20 hover:bg-white/10 rounded-sm h-12 uppercase tracking-widest">
                  {t("cta.browsePlayers")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ContactForm />

      <Footer />
    </div>
  );
}
